import { useCallback, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'

import Eye, { EyeRef } from '@/components/Eye/Eye'
import UIParticles from '@/components/Particles/UIParticles'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import useTracking from '@/composables/useTracking'

import LoadingExitSwirl from './LoadingExitSwirl'
import LoadingFooter from './LoadingFooter'
import LoadingStep from './LoadingStep'

export type LoadingProps = React.HTMLProps<HTMLDivElement> & {
  isVisible?: boolean
}

export default function Loading({ className, isVisible }: LoadingProps) {
  const { trackImpression } = useTracking()

  const { actor, persona } = useAppState()
  const { gsap } = useGSAP()

  const eyeRef = useRef<EyeRef>(null)
  const elementRef = useRef<HTMLDivElement>(null)

  const [isFooterVisible, setFooterVisible] = useState<boolean>(false)
  const [step, setStep] = useState<number>()

  const onEnter = useCallback(() => {
    trackImpression({
      unitName: 'loading.proceed',
      unitLocation: null,
    })

    const ctx = gsap.context(() => {
      const gap = 0.6
      const timeline = gsap
        .timeline()
        .addLabel('start', 0.1)
        .add(() => setFooterVisible(true), 'start')
        .add(() => setStep(0), 'start')
        .add(() => eyeRef.current?.open({}), `start`)

      for (let i = 0; i < 4; i++) {
        timeline.addLabel(`step${i}`, `start+=${gap * i}`)
        if (i < 3) {
          timeline
            .addLabel(`close${i}`, `step${i}+=${gap}`)
            .add(() => eyeRef.current?.close({ blink: false }), `close${i}`)
            .add(() => setStep(i + 1), `close${i}+=0.2`)
            .add(() => eyeRef.current?.open({}), `close${i}+=0.2`)
        }
      }

      timeline
        .addLabel('final', 'step3+=1.9')
        .add(() => setFooterVisible(false), 'final')
        .add(() => eyeRef.current?.close({ blink: true }), 'final')
        .set(elementRef.current, { opacity: 0, immediateRender: false }, 'final+=0.75')
        .add(() => actor.send({ type: 'LOADED' }), 'final+=0.75')
    })

    return () => ctx.kill()
  }, [gsap, eyeRef, elementRef, actor, trackImpression])

  return (
    <Transition
      in={isVisible}
      appear
      nodeRef={elementRef}
      mountOnEnter
      unmountOnExit
      onEnter={onEnter}
      timeout={{
        enter: 400,
        exit: 0,
      }}
    >
      <div
        data-testid='loading'
        ref={elementRef}
        className={`Loading relative grid pointer-events-none ${className ? className : ''}`}
      >
        <Eye
          ref={eyeRef}
          className='relative'
          defaultOpen={false}
          isLargeMobile
          clipOnMobile={true}
          childrenBackground={<div className='absolute top-0 left-0 w-full h-full bg-dark-cream' />}
          childrenBetween={
            step === 3 && (
              <>
                <UIParticles
                  delay={0}
                  className='absolute top-0 left-0 w-full h-full'
                  max={12}
                  exclude={{ colors: ['coral'] }}
                />
                {step === 3 && <LoadingExitSwirl />}
              </>
            )
          }
        >
          <div className='relative w-full h-full flex items-center justify-center'>
            {step === 0 && <LoadingStep index={0} />}
            {step === 1 && <LoadingStep index={1} />}
            {step === 2 && <LoadingStep index={2} />}
            {step === 3 && <LoadingStep index={3} />}
          </div>
        </Eye>

        <LoadingFooter isVisible={isFooterVisible} className='p-20 fixed z-100 left-0 bottom-0' />

        {/* <LoadingVideo source={{
          mp4: `src/assets/videos/interlude/${persona}_intro.mp4`,
          webm: `src/assets/videos/interlude/${persona}_intro.webm`,
        }} />
        <LoadingVideo source={{
          mp4: `src/assets/videos/interlude/${persona}_outro.mp4`,
          webm: `src/assets/videos/interlude/${persona}_outro.webm`,
        }} /> */}
      </div>
    </Transition>
  )
}
